import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import {
  Calendar, MapPin, ExternalLink, Users2, Loader2, Clock, ArrowLeft, Nfc,
  Sparkles, Trophy, Store,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";

import { LiveWall }          from "@/components/event/LiveWall";
import { AttendeeDirectory } from "@/components/event/AttendeeDirectory";
import { Leaderboard }       from "@/components/event/Leaderboard";
import { SponsorBooths }     from "@/components/event/SponsorBooths";

/**
 * /e/:slug — public event landing (auto-loaded when a bound badge is tapped).
 * Thin shell that composes the 4 tab components. Deep-link: ?spin=<booth_id>
 * auto-opens the SponsorBooths tab with the spin wheel pre-launched.
 */
export default function EventPublic() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [e, setE] = useState(null);
  const [err, setErr] = useState(null);

  const spinBoothId = searchParams.get("spin");
  const [tab, setTab] = useState(spinBoothId ? "booths" : "live");

  useEffect(() => {
    setErr(null);
    api.get(`/nfc/events/${slug}`).then(r => setE(r.data)).catch(() => setErr("Event not found"));
  }, [slug]);

  useSeo({
    title: e ? `${e.name} · schedule + speakers · find2near` : "Event · find2near",
    description: e?.venue ? `${e.name} · ${e.venue}${e.city ? " · " + e.city : ""}` : "Event schedule and speaker bios.",
    canonical: `/e/${slug}`,
  });

  if (err) return (
    <div className="min-h-screen grid place-items-center p-4">
      <div className="text-center">
        <Calendar className="mx-auto text-stone-400" size={44}/>
        <div className="font-black text-2xl text-stone-900 mt-3">{err}</div>
        <Link to="/nfc" className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-fuchsia-700 hover:underline"><ArrowLeft size={13}/> NFC hub</Link>
      </div>
    </div>
  );
  if (!e) return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-stone-400"/></div>;

  const isOwner = user && user.user_id === e.owner_user_id;
  const sortedSessions = [...(e.sessions || [])].sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || ""));

  return (
    <div className="min-h-screen bg-stone-50" data-testid="event-public-page">
      {/* Banner */}
      <div className="relative h-56 bg-gradient-to-br from-fuchsia-700 to-purple-900 overflow-hidden">
        {e.banner_url && <img src={e.banner_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40"/>}
        <div className="relative max-w-3xl mx-auto px-4 py-6 text-white h-full flex flex-col justify-end">
          <div className="text-[11px] uppercase tracking-widest font-black opacity-80 flex items-center gap-1"><Nfc size={12}/> Event · programmed badge</div>
          <h1 className="heading text-3xl md:text-4xl font-black mt-1 drop-shadow">{e.name}</h1>
          <div className="text-sm opacity-90 mt-1 flex flex-wrap items-center gap-2">
            <Calendar size={14}/> {e.starts_on} → {e.ends_on}
            {e.venue && <><MapPin size={14} className="ml-2"/> {e.venue}{e.city && `, ${e.city}`}</>}
            {e.website && <a href={e.website} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 rounded-full bg-white/20 hover:bg-white/30 px-2 py-0.5 text-xs font-black">Site <ExternalLink size={10}/></a>}
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Sessions */}
        {sortedSessions.length > 0 && (
          <section data-testid="event-sessions">
            <div className="text-[11px] uppercase tracking-widest font-black text-stone-500 mb-2 flex items-center gap-1"><Clock size={12}/> Schedule</div>
            <div className="rounded-3xl bg-white border border-stone-200 divide-y divide-stone-100 overflow-hidden">
              {sortedSessions.map(s => (
                <div key={s.session_id} className="p-3" data-testid={`session-${s.session_id}`}>
                  <div className="text-[11px] font-mono text-stone-500">{(s.starts_at || "").slice(0, 16).replace("T", " ")} → {(s.ends_at || "").slice(11, 16)}</div>
                  <div className="font-bold text-stone-900">{s.title}</div>
                  <div className="text-[11px] text-stone-500 mt-0.5">
                    {s.room && <span className="mr-2">🚪 {s.room}</span>}
                    {(s.speaker_names || []).length > 0 && <>👤 {s.speaker_names.join(", ")}</>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Speakers */}
        {(e.speakers || []).length > 0 && (
          <section data-testid="event-speakers">
            <div className="text-[11px] uppercase tracking-widest font-black text-stone-500 mb-2 flex items-center gap-1"><Users2 size={12}/> Speakers</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {e.speakers.map((sp, i) => (
                <div key={i} className="rounded-3xl bg-white border border-stone-200 p-3 flex gap-3" data-testid={`speaker-${i}`}>
                  {sp.photo ? (
                    <img src={sp.photo} alt={sp.name} className="w-14 h-14 rounded-2xl object-cover shrink-0"/>
                  ) : (
                    <div className="w-14 h-14 rounded-2xl bg-fuchsia-50 grid place-items-center shrink-0 text-fuchsia-600 font-black text-lg">
                      {(sp.name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-stone-900 truncate">{sp.name}</div>
                    <div className="text-[11px] text-stone-500 truncate">{sp.title}{sp.company && ` · ${sp.company}`}</div>
                    {sp.bio && <p className="text-xs text-stone-600 mt-1 line-clamp-3">{sp.bio}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {(sortedSessions.length === 0 && (e.speakers || []).length === 0) ? (
          <div className="rounded-3xl bg-white border border-stone-200 p-6 text-center text-sm text-stone-500">
            The organiser hasn't added a schedule or speakers yet — check back closer to the event.
          </div>
        ) : null}

        {/* Tabs */}
        <div className="flex gap-1.5 pt-2 flex-wrap">
          {[
            { k: "live",        l: "Live wall",     I: Sparkles },
            { k: "directory",   l: "Attendees",     I: Users2 },
            { k: "leaderboard", l: "Tap champions", I: Trophy },
            { k: "booths",      l: "Sponsor booths", I: Store },
          ].map(({ k, l, I }) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`text-xs font-black rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 border ${tab === k ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"}`}
              data-testid={`event-tab-${k}`}
            >
              <I size={12}/> {l}
            </button>
          ))}
        </div>

        {tab === "live"        && <LiveWall eventId={e.event_id}/>}
        {tab === "directory"   && <AttendeeDirectory eventId={e.event_id} isOwner={isOwner}/>}
        {tab === "leaderboard" && <Leaderboard       eventId={e.event_id} isOwner={isOwner}/>}
        {tab === "booths"      && <SponsorBooths     eventId={e.event_id} eventSlug={e.slug} isOwner={isOwner} autoSpinBoothId={spinBoothId}/>}

        <div className="text-center text-[11px] text-stone-500 pt-2">{e.badges_issued || 0} attendees have been programmed · Powered by find2near</div>
      </div>
    </div>
  );
}
